import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';

export const TimeTravelPanel = ({ history, onSelectDate }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [historicalMood, setHistoricalMood] = useState(null);

  // Group history by date and show ALL available days
  const dateGroups = useMemo(() => {
    if (!history?.length) return [];
    const groups = {};
    
    history.forEach(item => {
      const date = new Date(item.timestamp).toLocaleDateString('en-CA'); // YYYY-MM-DD format
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
    });
    
    // Return ALL dates, sorted newest first
    return Object.entries(groups)
      .map(([date, items]) => ({
        date,
        displayDate: new Date(date).toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        }),
        shortDate: new Date(date).toLocaleDateString('en-US', { 
          weekday: 'short',
          day: 'numeric' 
        }),
        avgIntensity: items.reduce((sum, i) => sum + (i.intensity || 0.5), 0) / items.length,
        dominantColor: items[Math.floor(items.length / 2)]?.color || '#6b7280',
        dominantAdjective: items[Math.floor(items.length / 2)]?.adjective || 'unknown',
        count: items.length
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [history]);

  // Select the most recent date by default
  useEffect(() => {
    if (dateGroups.length > 0 && !selectedDate) {
      setSelectedDate(dateGroups[0].date);
      setHistoricalMood(dateGroups[0]);
    }
  }, [dateGroups, selectedDate]);

  const handleDateClick = (dateGroup) => {
    setSelectedDate(dateGroup.date);
    setHistoricalMood(dateGroup);
    if (onSelectDate) onSelectDate(dateGroup);
  };

  // Navigation for when there are many days
  const [scrollPosition, setScrollPosition] = useState(0);
  const visibleDays = 7;
  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < dateGroups.length - visibleDays;

  if (!dateGroups.length) {
    return (
      <div className="time-travel-v2" data-testid="time-travel-panel">
        <div className="tt-empty">
          <Clock size={18} style={{ opacity: 0.4 }} />
          <span>No historical data yet</span>
        </div>
      </div>
    );
  }

  const visibleGroups = dateGroups.slice(scrollPosition, scrollPosition + visibleDays);

  return (
    <div className="time-travel-v2" data-testid="time-travel-panel">
      {/* Header */}
      <div className="tt-header">
        <div className="tt-title-row">
          <Clock size={14} />
          <span className="tt-title">Time Travel</span>
        </div>
        <span className="tt-count">{dateGroups.length} day{dateGroups.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Selected mood display */}
      {historicalMood && (
        <div className="tt-selected">
          <motion.div 
            className="tt-selected-orb"
            style={{ backgroundColor: historicalMood.dominantColor }}
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            key={historicalMood.date}
          />
          <div className="tt-selected-info">
            <span className="tt-selected-date">{historicalMood.displayDate}</span>
            <span 
              className="tt-selected-mood"
              style={{ color: historicalMood.dominantColor }}
            >
              {historicalMood.dominantAdjective}
            </span>
          </div>
          <div className="tt-selected-stats">
            <span className="tt-stat">{historicalMood.count} readings</span>
            <span className="tt-stat">{Math.round(historicalMood.avgIntensity * 100)}%</span>
          </div>
        </div>
      )}

      {/* Day selector - horizontal scrolling list */}
      <div className="tt-days-container">
        {dateGroups.length > visibleDays && (
          <button 
            className={`tt-nav-btn tt-nav-left ${!canScrollLeft ? 'disabled' : ''}`}
            onClick={() => setScrollPosition(Math.max(0, scrollPosition - 1))}
            disabled={!canScrollLeft}
          >
            <ChevronLeft size={14} />
          </button>
        )}
        
        <div className="tt-days-list">
          {visibleGroups.map((group, idx) => (
            <motion.button
              key={group.date}
              className={`tt-day ${selectedDate === group.date ? 'active' : ''}`}
              onClick={() => handleDateClick(group)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
            >
              <div 
                className="tt-day-dot"
                style={{ backgroundColor: group.dominantColor }}
              />
              <span className="tt-day-label">{group.shortDate}</span>
              <span 
                className="tt-day-mood"
                style={{ color: group.dominantColor }}
              >
                {group.dominantAdjective.slice(0, 8)}
              </span>
            </motion.button>
          ))}
        </div>

        {dateGroups.length > visibleDays && (
          <button 
            className={`tt-nav-btn tt-nav-right ${!canScrollRight ? 'disabled' : ''}`}
            onClick={() => setScrollPosition(Math.min(dateGroups.length - visibleDays, scrollPosition + 1))}
            disabled={!canScrollRight}
          >
            <ChevronRight size={14} />
          </button>
        )}
      </div>

      {/* Quick stats row */}
      {dateGroups.length > 1 && (
        <div className="tt-quick-stats">
          {dateGroups.slice(0, 7).map((group) => (
            <div 
              key={group.date}
              className={`tt-quick-day ${selectedDate === group.date ? 'active' : ''}`}
              onClick={() => handleDateClick(group)}
              title={`${group.displayDate}: ${group.dominantAdjective}`}
            >
              <div 
                className="tt-quick-bar"
                style={{ 
                  height: `${group.avgIntensity * 100}%`,
                  backgroundColor: group.dominantColor
                }}
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Summary note */}
      {dateGroups.length < 7 && (
        <div className="tt-note" style={{ fontSize: '9px', color: 'var(--apple-text-tertiary)', textAlign: 'center', marginTop: '-4px' }}>
          {dateGroups.length} of 7+ days tracked — more data accumulates over time
        </div>
      )}
    </div>
  );
};

export default TimeTravelPanel;
